// Tote math - the client's exact mirror of og-games-api
// app/games/horseracing/rules.py. Audited by og-games-api
// scripts/parity_audit.py, so keep the literals in sync with the server.
//
// Every probability is an exact rational over the public integer form
// weights (Plackett-Luce), carried as BigInt {num, den}; prices floor to
// hundredths with the same integer arithmetic the settle path uses, so the
// odds printed on this tote are exactly what the server pays.

import { displayHorseName } from './horse-name.js';

export const WEIGHT_MIN = 10;
export const WEIGHT_MAX = 40;
export const MIN_ODDS_H = 101;
export const MAX_ODDS_H = 1000000;
export const DEFAULT_RTP_BP = 9600;

// type -> picks / ordered / label (mirrors rules.BET_TYPES + BET_LABELS).
export const BET_TYPES = {
  win: { picks: 1, ordered: false, label: 'Win' },
  place: { picks: 1, ordered: false, label: 'Place' },
  show: { picks: 1, ordered: false, label: 'Show' },
  exacta: { picks: 2, ordered: true, label: 'Exacta' },
  quinella: { picks: 2, ordered: false, label: 'Quinella' },
  trifecta: { picks: 3, ordered: true, label: 'Trifecta' },
};

export const BET_HELP = {
  win: 'finishes 1st',
  place: 'finishes 1st or 2nd',
  show: 'finishes in the top 3',
  exacta: '1st + 2nd in exact order',
  quinella: '1st + 2nd in either order',
  trifecta: '1st + 2nd + 3rd in exact order',
};

// Gait families (index -> display name); the scene maps the same index onto
// its animation clip families. Mirrors rules.BLOODLINES.
export const BLOODLINES = ['Apex', 'Ember', 'Nimbus', 'Zephyr'];

// Standard US saddlecloth palette by gate (post position 1..8). These are
// race-slot accents for cloths, bet chips, badges, labels, and optional light
// trails. They must never tint a horse's physical model/profile coat.
export const GATE_ACCENT_COLORS = [
  '#d5382e', '#f5f2e9', '#1c53b0', '#f2c230',
  '#1d7f3c', '#17181d', '#e07b1f', '#e77fb2',
];
export const GATE_ACCENT_TEXT = [
  '#fff', '#111', '#fff', '#111', '#fff', '#fff', '#fff', '#111',
];

// ------------------------------------------------------- exact rationals --
const gcd = (a, b) => (b ? gcd(b, a % b) : a);

function frac(num, den) {
  const g = gcd(num < 0n ? -num : num, den);
  return { num: num / g, den: den / g };
}

const add = (a, b) => frac(a.num * b.den + b.num * a.den, a.den * b.den);
const mul = (a, b) => frac(a.num * b.num, a.den * b.den);

function pWin(w, i) {
  const total = w.reduce((a, x) => a + BigInt(x), 0n);
  return frac(BigInt(w[i]), total);
}

function pPlace(w, i) {
  const total = w.reduce((a, x) => a + BigInt(x), 0n);
  let p = frac(BigInt(w[i]), total);
  for (let j = 0; j < w.length; j++) {
    if (j === i) continue;
    p = add(p, mul(frac(BigInt(w[j]), total), frac(BigInt(w[i]), total - BigInt(w[j]))));
  }
  return p;
}

function pShow(w, i) {
  const total = w.reduce((a, x) => a + BigInt(x), 0n);
  let p = pPlace(w, i);
  for (let j = 0; j < w.length; j++) {
    if (j === i) continue;
    for (let k = 0; k < w.length; k++) {
      if (k === i || k === j) continue;
      p = add(p, mul(
        mul(frac(BigInt(w[j]), total), frac(BigInt(w[k]), total - BigInt(w[j]))),
        frac(BigInt(w[i]), total - BigInt(w[j]) - BigInt(w[k])),
      ));
    }
  }
  return p;
}

function pExacta(w, i, j) {
  const total = w.reduce((a, x) => a + BigInt(x), 0n);
  return mul(frac(BigInt(w[i]), total), frac(BigInt(w[j]), total - BigInt(w[i])));
}

function pQuinella(w, i, j) {
  return add(pExacta(w, i, j), pExacta(w, j, i));
}

function pTrifecta(w, i, j, k) {
  const total = w.reduce((a, x) => a + BigInt(x), 0n);
  return mul(
    mul(frac(BigInt(w[i]), total), frac(BigInt(w[j]), total - BigInt(w[i]))),
    frac(BigInt(w[k]), total - BigInt(w[i]) - BigInt(w[j])),
  );
}

export function probability(weights, type, sel) {
  if (type === 'win') return pWin(weights, sel[0]);
  if (type === 'place') return pPlace(weights, sel[0]);
  if (type === 'show') return pShow(weights, sel[0]);
  if (type === 'exacta') return pExacta(weights, sel[0], sel[1]);
  if (type === 'quinella') return pQuinella(weights, sel[0], sel[1]);
  if (type === 'trifecta') return pTrifecta(weights, sel[0], sel[1], sel[2]);
  throw new Error(`unknown bet type ${type}`);
}

// ---------------------------------------------------------------- pricing --
export function priceH(p, rtpBp) {
  const raw = (BigInt(rtpBp) * p.den) / (100n * p.num);
  const clamped = raw < BigInt(MIN_ODDS_H) ? BigInt(MIN_ODDS_H)
    : raw > BigInt(MAX_ODDS_H) ? BigInt(MAX_ODDS_H) : raw;
  return Number(clamped);
}

export function betOddsH(weights, type, sel, rtpBp) {
  return priceH(probability(weights, type, sel), rtpBp);
}

/** The tote board: win/place/show odds per horse, in hundredths. */
export function oddsBoard(weights, rtpBp) {
  const n = weights.length;
  const board = { win: [], place: [], show: [] };
  for (let i = 0; i < n; i++) {
    board.win.push(betOddsH(weights, 'win', [i], rtpBp));
    board.place.push(betOddsH(weights, 'place', [i], rtpBp));
    board.show.push(betOddsH(weights, 'show', [i], rtpBp));
  }
  return board;
}

// -------------------------------------------------------------- resolution --
export function betWins(order, type, sel) {
  if (type === 'win') return order[0] === sel[0];
  if (type === 'place') return order.slice(0, 2).includes(sel[0]);
  if (type === 'show') return order.slice(0, 3).includes(sel[0]);
  if (type === 'exacta') return order[0] === sel[0] && order[1] === sel[1];
  if (type === 'quinella') {
    return (order[0] === sel[0] && order[1] === sel[1])
      || (order[0] === sel[1] && order[1] === sel[0]);
  }
  if (type === 'trifecta') {
    return order[0] === sel[0] && order[1] === sel[1] && order[2] === sel[2];
  }
  return false;
}

/** Display mirror of the server settle: per-line odds, wins and the round
 *  multiplier in hundredths (returnedH // total - identical flooring). */
export function settleTicket(bets, order, weights, rtpBp) {
  let total = 0;
  let returnedH = 0;
  const results = bets.map((b) => {
    const oddsH = betOddsH(weights, b.type, b.sel, rtpBp);
    const won = betWins(order, b.type, b.sel);
    const retH = won ? b.amount * oddsH : 0;
    total += b.amount;
    returnedH += retH;
    return { ...b, oddsH, won, returnCents: Math.floor(retH / 100) };
  });
  const multiplierH = total > 0 && returnedH > 0 ? Math.floor(returnedH / total) : 0;
  return { results, total, returnedH, multiplierH };
}

export const canonicalSel = (type, sel) => (
  BET_TYPES[type].ordered ? [...sel] : [...sel].sort((a, b) => a - b)
);

export const ticketKey = (type, sel) => `${type}:${canonicalSel(type, sel).join('-')}`;

// ----------------------------------------------------------------- display --
export const fmtOdds = (oddsH) => {
  const x = oddsH / 100;
  return `${x >= 100 ? Math.round(x) : x.toFixed(2)}x`;
};

export function betLabel(type, sel, field) {
  const names = sel.map((i) => (
    `#${i + 1} ${displayHorseName(field && field.horses[i] && field.horses[i].name)}`
  ));
  return `${BET_TYPES[type].label} \u00b7 ${names.join(BET_TYPES[type].ordered ? ' \u2192 ' : ' + ')}`;
}
